import { SimpleTreeView } from "@mui/x-tree-view-pro";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import hasChildren from "./helperfunctions";
import CustomTreeItem from "./CustomTreeItem";
import { Box, Divider, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { CancelOutlined, Search } from "@mui/icons-material";
import ActionMenu from '../menu/ActionMenu';
import GenericActionMenu from "../menu/GenericActionMenu";
import { useTheme } from '@mui/material';
import { DocumentIcon, FolderIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { breakpoints } from "../../theme/theme";
import styles from "../../styles/folderDrawerView/index.module.scss";
import { InputBox } from "../FormComponent";
import ModuleEmptyScreen from "../TestCase/ModuleEmptyScreen";

const DEFAULT_MODULE_TITLE = "All Test Cases";
const DEFAULT_TITLE = "Project Name";

const ModuleName = ({ name, fontSize, color, fontWeight = 500 }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = React.useRef(null);

  React.useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [name]);

  return (
    <Tooltip title={isOverflowing ? name : ''} arrow placement="top">
      <Typography
        ref={textRef}
        variant="contentSmall"
        className={`${styles.moduleNameContainer} ellipsis-container`}
        sx={{
          fontWeight,
          color,
        }}
      >
        {name}
      </Typography>
    </Tooltip>
  );
};

const FolderDrawerTreeView = forwardRef(({
  data,
  moduleTitle = DEFAULT_MODULE_TITLE,
  title = DEFAULT_TITLE,
  onLeafNodeClick,
  rowActionButtons = (() => { return [] }),
  headerActionButtons = [],
  disableHeaderActions = true,
  disableActions = true,
  selected = ['default'],
  titleLabel = 'Project Name',
  setSelected = () => { },
  hideHeader = false,
  showSearch = false,
  smallView = false,
  isDataLoading = false,
  updateIsExpanded = () => { },
  highlightedNodeIds = null, // Array of IDs to keep highlighted, others are dimmed
  moduleEmptyPrompt = null,
}, ref) => {

  const [expanded, setExpanded] = useState([]);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const theme = useTheme();

  useEffect(() => {
    setFilteredData([...data]);
  }, [data])

  useImperativeHandle(ref, () => ({
    triggerExpandAll: expandAll,
    triggerCollapseAll: collapseAll,
    triggerExpandOneNode: expandOneNode,
  }), []);

  const expandOneNode = (nodeId) => {
    if (!expanded.includes(String(nodeId))) {
      setExpanded([...expanded, String(nodeId)]);
    }
  }

  const handleNodeToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  const getAllIds = (nodes) => {
    let ids = [];
    nodes.forEach(node => {
      ids.push(String(node.id));
      if (node.children?.length) {
        ids = [...ids, ...getAllIds(node.children)];
      }
    });
    return ids;
  };

  // Expand all nodes
  const expandAll = (treeData) => {
    const allIds = getAllIds(treeData);
    setExpanded(allIds);
    updateIsExpanded(true);
  };

  // Collapse all nodes
  const collapseAll = () => {
    setExpanded([]);
    updateIsExpanded(false);
  };

  const filterTreeData = (nodes, query) => {
    if (!query.trim()) return nodes;

    return nodes
      .map((node) => {
        if (node.name.toLowerCase().includes(query.toLowerCase())) {
          return node;
        }
        if (node.children?.length) {
          const filteredChildren = filterTreeData(node.children, query);
          if (filteredChildren.length > 0) {
            return { ...node, children: filteredChildren };
          }
        }
        return null;
      })
      .filter(Boolean);
  };

  const handleSearchTerm = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (value.trim() === '') {
      setFilteredData(data);
    } else {
      const filtered = filterTreeData(data, value);
      setFilteredData(filtered);

      // Expand all parent nodes of matched results for better visibility
      if (filtered.length) {
        const allNodeIds = getAllIds(filtered);
        setExpanded([...allNodeIds]);
      }
    }
  };

  const renderTree = (nodes, level = 1) => {
    return nodes.map((node) => {
      if (!node?.id) {
        console.error("Node is missing an id:", node);
        return null;
      }

      const isLeafNode = !hasChildren(node);
      const isExpanded = expanded.includes(String(node.id));
      const isHovered = hoveredNodeId === node.id;

      let countValue;
      if (isLeafNode) {
        countValue = node.individualcount;
      } else {
        countValue = isExpanded ? node.individualcount : node.totalcount;
      }

      // Colors based on state
      const getColors = () => {
        if (isExpanded) {
          return {
            textColor: '#1e3a8a', // blue-900
            countBg: '#dbeafe', // blue-100
            countText: '#1d4ed8', // blue-700
            countBorder: '#93c5fd', // blue-200
            iconBg: '#dbeafe', // blue-100
            iconColor: '#2563eb', // blue-600
          };
        } else if (isHovered) {
          return {
            textColor: '#0f172a', // slate-900
            countBg: '#e2e8f0', // slate-200
            countText: '#475569', // slate-600
            iconBg: '#dbeafe', // blue-50
            iconColor: '#2563eb', // blue-600
          };
        } else {
          return {
            textColor: level > 1 ? '#374151' : '#1e293b', // slate-700 for children, slate-800 for parents
            countBg: '#f1f5f9', // slate-100
            countText: '#64748b', // slate-600
            iconBg: '#f1f5f9', // slate-100
            iconColor: '#64748b', // slate-600
          };
        }
      };

      const colors = getColors();

      return (
        <CustomTreeItem
          id={node.id}
          key={node.id}
          itemId={String(node.id)}
          level={level}
          isExpanded={isExpanded}
          className={styles[`child-${level}`]}
          sx={{
            opacity: 1, // Always show at full opacity
          }}
          label={
            <Box
              onMouseEnter={() => setHoveredNodeId(node.id)}
              onMouseLeave={() => setHoveredNodeId(null)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                minHeight: level > 1 ? '20px' : '18px', // Smaller height for children
                gap: '6px',
                [`@media (min-width:${breakpoints.xl}px)`]: {
                  py: "3px",
                },
              }}
              onClick={(e) => {
                if (e.target.closest(".module-action-menu") === null) {
                  onLeafNodeClick(node.id, node.name);
                }
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: level > 1 ? 1 : 1, // Smaller gap for children
                  overflow: "hidden",
                  flex: 1,
                }}
              >
                <IconButton
                  disableRipple
                  sx={{
                    padding: 0,
                    cursor: !isLeafNode ? 'pointer' : 'default'
                  }}
                  onClick={e => {
                    if (!isLeafNode) {
                      e.stopPropagation();
                      if (isExpanded) {
                        setExpanded(expanded.filter(id => id !== String(node.id)));
                      } else {
                        setExpanded([...expanded, String(node.id)]);
                      }
                    }
                  }}
                >
                  {!isLeafNode ? (
                    isExpanded ? (
                      <ChevronDownIcon
                        className="icon-small"
                        style={{ color: '#2563eb' }}
                      />
                    ) : (
                      <ChevronRightIcon
                        className="icon-small"
                        style={{ color: isHovered ? '#2563eb' : '#64748b' }}
                      />
                    )
                  ) : (
                    <span className={level > 2 ? "icon-x-small" : "icon-small"} />
                  )}
                </IconButton>

                <Box
                  className={'icon-cover-small'}
                  sx={{
                    backgroundColor: isHovered ? (colors.iconBg) : colors.iconBg,
                    border: isHovered && level > 1 ? '1px solid #e2e8f0' : 'none',
                  }}
                >
                  {level > 1 ? (
                    <DocumentIcon
                      className="icon-small"
                      style={{ color: colors.iconColor }}
                    />
                  ) : (
                    <FolderIcon
                      className="icon-small"
                      style={{ color: colors.iconColor }}
                    />
                  )}
                </Box>

                <ModuleName
                  name={node.name}
                  color={isHovered && level > 1 ? '#0f172a' : colors.textColor}
                  fontWeight={level > 1 ? 400 : 500}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="contentSmallSemiBold"
                  style={{
                    marginLeft: 8,
                    color: countValue === 0 ? '#dc2626' : (isHovered && level > 1 ? '#475569' : colors.countText),
                    backgroundColor: colors.countBg,
                    padding: level > 1 ? '2px 6px' : '2px 6px',
                    borderRadius: '9999px',
                    border: isExpanded ? `1px solid ${colors.countBorder}` : 'none',
                  }}
                >
                  {countValue ?? 0}
                </Typography>
                {!disableActions && (
                  <Box
                    className="module-action-menu"
                    sx={{
                      opacity: hoveredNodeId === node.id ? 1 : 0,
                      transition: 'opacity 0.2s',
                      marginLeft: 1,
                      pointerEvents: hoveredNodeId === node.id ? 'auto' : 'none',
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <ActionMenu menuItems={rowActionButtons(node)} rowData={node} />
                  </Box>
                )}
              </Box>
            </Box>
          }
        >
          {hasChildren(node) && renderTree(node.children, level + 1)}
        </CustomTreeItem>
      );
    });
  };

  function handleClearSearchTerm() {
    setSearchTerm('');
    setFilteredData(data)
  }

  const showModuleEmpty = Boolean(moduleEmptyPrompt) && !isDataLoading && data.length === 0;

  return (
    <Box className={`${styles.container} bg-white`}>
      {!hideHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Left: Blue dot + Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1.25 }}>
            <Box sx={{ minWidth: 6, width: 6, height: 6, backgroundColor: '#3b82f6', borderRadius: '50%' }} />
            <Typography
              variant="componentTitle"
              className={`ellipsis-container-line-2 ${styles.titleContainer}`}
              sx={{
                color: '#0f172a',
              }}
              title={title}
            >
              {title}
            </Typography>
          </Box>
          {/* Right: Header actions */}
          {!disableHeaderActions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '10px' }}>
              <GenericActionMenu menuItems={headerActionButtons} />
            </Box>
          )}
        </Box>
      )}
      <Divider className={`${styles.divider}`} />
      <Box className={`${styles.innerContainer}`}>
        {showSearch && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px',
          }}>
            <>
              <InputBox
                label="Modules"
                value={searchTerm}
                onChange={handleSearchTerm}
                variant="outlined"
                placeholder="Search Modules"
                startIcon={<Search className="icon-small" />}
                endIcon={searchTerm && <CancelOutlined className="icon-small" />}
                onEndIconClick={handleClearSearchTerm}
              />
            </>
          </div>

        )}

        {isDataLoading ? null : showModuleEmpty ? (
          <Box className={styles.moduleEmptyWrap}>
            <ModuleEmptyScreen onAddModule={moduleEmptyPrompt.onAddModule} />
          </Box>
        ) : (
        <SimpleTreeView
          id="module-explorer"
          aria-label="modules-tree"
          sx={{
            overflowX: "hidden",
            overflowY: "auto",
            px: "4px",
            py: "0px",
            flex: 1,

            "& .MuiTreeItem-iconContainer": {
              display: 'none !important',
            },

            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.05)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.3)',
              },
            },

          }}
          expandedItems={expanded}
          onExpandedItemsChange={handleNodeToggle}
          selectedItems={selected}
          onSelectedItemsChange={(event, newSelected) => { setSelected(newSelected) }}
        >
          {!hideHeader && (
            <>
              <CustomTreeItem
                itemId="default"
                level={1}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      height: 'max-content',
                      minHeight: '25px',
                      gap: '6px',
                      [`@media (min-width:${breakpoints.xl}px)`]: {
                        py: "0px",
                        minHeight: '35px',
                      },
                    }}
                    onClick={(e) => {
                      if (e.target.closest(".module-action-menu") === null) {
                        onLeafNodeClick("default", moduleTitle);
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        overflow: "hidden",
                        flex: 1,
                      }}
                    >
                      <span className="icon-small" />
                      <Box
                        className={'icon-cover-small bg-blue100'}
                      >
                        <FolderIcon
                          className="icon-small blue600"
                        />
                      </Box>
                      <ModuleName
                        name={moduleTitle}
                        color={'#1e293b'} // slate-800
                        fontWeight={500}
                      />
                    </Box>
                  </Box>
                }
              />
              {/* <Divider sx={{ borderColor: '#e5e7eb', my: 1 }} /> */}
            </>
          )}
          {renderTree(filteredData)}
        </SimpleTreeView>
      )}
      </Box>
    </Box>
  );
});

export default FolderDrawerTreeView;